import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getCrmBrandContactDetailsEndpointIdentifier } from './use-get-crm-brand-contact-details-api';

const endpoint = 'ums/v1/crm/management/brands/contacts';
export const updateContactBrandEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  id: number;
  brandId: number;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdateContactBrandApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ id, brandId }: TVariables) => {
    try {
      const { data } = await http.patch(`${endpoint}/${id}/brand`, { brandId });

      toast({
        description: 'Contact brand has been updated successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    mutationKey: `${endpoint}/brand`,
    onSuccess: (data, variables, context) => {
      // Automatically refetch the list data
      queryClient.invalidateQueries([getCrmBrandContactDetailsEndpointIdentifier]);

      // Call user-provided onSuccess callback if exists
      if (userOnSuccess) {
        userOnSuccess(data, variables, context);
      }
    },
    ...restOptions,
  });
}
